import { LoanPurpose } from './generated/graphql-types'
import { Routes } from './Routes'

export enum QuestionSetSlug {
  AmazonBenefits = 'amazon-benefits',
  ApplyPurchase = 'apply-purchase',
  ApplyRefinance = 'apply-refinance',
  Compare = 'compare-ntl',
  CreditConsent = 'credit-consent',
  DecsDems = 'declarations-demographics',
  FannieMae = 'fannie-mae',
  FanniePlaid = 'fannie-plaid',
  FullPurchase = 'full-purchase',
  FullRefinance = 'full-refinance',
  HomeEquity = 'home-equity',
  LeadPurchase = 'lead-purchase',
  LeadRefinance = 'lead-refinance',
  NTL = 'ntl',
  PhoneApp = 'phone-app',
  Plaid = 'plaid',
  PrePricingPurchase = 'pre-pricing-purchase',
  PrePricingRefinance = 'pre-pricing-refinance',
  PurchaseFastPass = 'purchase-fastpass',
  RhApplyPurchase = 'rh-apply-purchase',
  RhApplyRefinance = 'rh-apply-refinance',
  RhConcierge = 'rh-concierge',
  RhPrePricingPurchase = 'rh-pre-pricing-purchase',
  RhPrePricingRefinance = 'rh-pre-pricing-refinance',
  RhHomeEquity = 'rh-home-equity',
  SimpleV1 = 'simple-v1',
  SimpleV1Results = 'simple-v1-results',
  SimpleV2History = 'simple-v2-history',
  SimpleV2Purchase = 'simple-v2-purchase',
  SimpleV2Refinance = 'simple-v2-refinance',
  SimpleV2PurchaseFannie = 'simple-v2-purchase-fannie',
  SimpleV2RefinanceFannie = 'simple-v2-refinance-fannie',
}

// Dedicated route segment for the progressive lead flow welcome page:
export const LEAD_WELCOME_ROUTE = 'welcome-lead'

export enum SplitFlowSegments {
  PrePricing = 'prePricing',
  Apply = 'apply',
}

export enum TaskName {
  ApplyFlow = 'apply-flow',
  CollectSsn = 'collect-ssn',
  DeclarationsAndDemographics = 'declarations-demographics',
  AppraisalPayment = 'appraisal-payment',
  GetRealtorInfo = 'get-realtor-info',
  SignLeDisclosure = 'sign-le-disclosure',
  SignRevisedLeDisclosure = 'sign-revised-le-disclosure',
  SignCdDisclosure = 'sign-cd-disclosure',
  SignRevisedCdDisclosure = 'sign-revised-cd-disclosure',
  SimpleV2History = 'simple-v2-history',
}

type SplitMapType = Record<
  LoanPurpose,
  Record<SplitFlowSegments, QuestionSetSlug>
>

export const splitFlowSlugMap: SplitMapType = {
  [LoanPurpose.Refinance]: {
    [SplitFlowSegments.PrePricing]: QuestionSetSlug.PrePricingRefinance,
    [SplitFlowSegments.Apply]: QuestionSetSlug.ApplyRefinance,
  },
  [LoanPurpose.Purchase]: {
    [SplitFlowSegments.PrePricing]: QuestionSetSlug.PrePricingPurchase,
    [SplitFlowSegments.Apply]: QuestionSetSlug.ApplyPurchase,
  },
  [LoanPurpose.HomeEquity]: {
    [SplitFlowSegments.PrePricing]: QuestionSetSlug.HomeEquity,
    [SplitFlowSegments.Apply]: QuestionSetSlug.HomeEquity,
  },
}

// Initial SB flows a client will see
export const prePricingQuestionSetsMap = {
  [LoanPurpose.Refinance]: {
    standard: QuestionSetSlug.PrePricingRefinance,
    robinhoodGold: QuestionSetSlug.RhPrePricingRefinance,
  },
  [LoanPurpose.HomeEquity]: {
    standard: QuestionSetSlug.HomeEquity,
    robinhoodGold: QuestionSetSlug.RhHomeEquity,
  },
  [LoanPurpose.Purchase]: {
    standard: QuestionSetSlug.PrePricingPurchase,
    robinhoodGold: QuestionSetSlug.RhPrePricingPurchase,
  },
} as const

// SB flows a client will see after pre-pricing is complete
export const applyQuestionSetsMap = {
  [LoanPurpose.Refinance]: {
    standard: QuestionSetSlug.ApplyRefinance,
    robinhoodGold: QuestionSetSlug.RhApplyRefinance,
  },
  [LoanPurpose.HomeEquity]: {
    standard: QuestionSetSlug.HomeEquity,
    robinhoodGold: QuestionSetSlug.HomeEquity, // No RH variant for HomeEquity YET
  },
  [LoanPurpose.Purchase]: {
    standard: QuestionSetSlug.ApplyPurchase,
    robinhoodGold: QuestionSetSlug.RhApplyPurchase,
  },
} as const

/**
 * Get the question set slug based on the loan purpose and the status of the first flow (pre-pricing)
 * pre-pricing-purchase -> apply-purchase
 * pre-pricing-refinance -> apply-refinance
 * Home equity uses a single questionnaire for both segments.
 *
 * Special cases for Robinhood Gold Members
 * rh-pre-pricing-purchase -> rh-apply-purchase
 * rh-pre-pricing-refinance -> rh-apply-refinance
 */
export const getQuestionSetSlug = ({
  isPrePricingIncomplete,
  loanPurpose,
  isRobinhoodGoldMember = false,
  defaultSlug = QuestionSetSlug.PrePricingPurchase,
}: {
  isPrePricingIncomplete: boolean
  loanPurpose: LoanPurpose
  isRobinhoodGoldMember?: boolean
  defaultSlug?: QuestionSetSlug
}) => {
  const segment = isPrePricingIncomplete
    ? SplitFlowSegments.PrePricing
    : SplitFlowSegments.Apply

  const flowMap = {
    [LoanPurpose.Purchase]: {
      [SplitFlowSegments.PrePricing]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhPrePricingPurchase
        : QuestionSetSlug.PrePricingPurchase,
      [SplitFlowSegments.Apply]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhApplyPurchase
        : QuestionSetSlug.ApplyPurchase,
    },
    [LoanPurpose.Refinance]: {
      [SplitFlowSegments.PrePricing]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhPrePricingRefinance
        : QuestionSetSlug.PrePricingRefinance,
      [SplitFlowSegments.Apply]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhApplyRefinance
        : QuestionSetSlug.ApplyRefinance,
    },
    [LoanPurpose.HomeEquity]: {
      [SplitFlowSegments.PrePricing]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhHomeEquity
        : QuestionSetSlug.HomeEquity,
      [SplitFlowSegments.Apply]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhHomeEquity
        : QuestionSetSlug.HomeEquity,
    },
  }

  return flowMap[loanPurpose]?.[segment] ?? defaultSlug
}

/**
 * Resolve the standard pre-pricing question set for a loan purpose.
 * Falls back to purchase pre-pricing when the purpose is missing or unmapped.
 */
export function getStandardPrePricingQuestionSet(
  loanPurpose?: LoanPurpose
): QuestionSetSlug {
  return (
    prePricingQuestionSetsMap[loanPurpose as LoanPurpose]?.standard ??
    QuestionSetSlug.PrePricingPurchase
  )
}

/**
 * Assemble the questionnaire pre-pricing redirect URL for a new application.
 * The transit_id param is omitted entirely when no transit id is available,
 * rather than emitting a dangling `&transit_id=`.
 */
export function buildPrePricingRedirectUrl({
  questionnaireBaseUrl,
  questionSet,
  applicationGuid,
  transitId,
}: {
  questionnaireBaseUrl: string
  questionSet: QuestionSetSlug
  applicationGuid: string
  transitId?: string
}): string {
  const baseUrl =
    `${questionnaireBaseUrl}/${Routes.APP_1003}` +
    `?question-set=${questionSet}` +
    `&application-guid=${applicationGuid}`

  return transitId ? `${baseUrl}&transit_id=${transitId}` : baseUrl
}

/**
 * Build the servicer-lead apply URL that email, rate-widget, and direct-mail
 * links point to. The endpoint looks up the servicer lead by uuid, creates or
 * reuses an application, prefills it, and redirects to Alfie.
 */
export function buildServicerApplyUrl({
  apiBaseUrl,
  uuid,
}: {
  apiBaseUrl: string
  uuid: string
}): string {
  return `${apiBaseUrl}/servicer-lead/apply?uuid=${uuid}`
}

export const SERVICER_LOAN_PURPOSE_PARAM = 'loan_purpose'

export const servicerLoanPurposeParamMap: Record<string, LoanPurpose> = {
  refinance: LoanPurpose.Refinance,
  refi: LoanPurpose.Refinance,
  home_equity: LoanPurpose.HomeEquity,
  purchase: LoanPurpose.Purchase,
}

/**
 * Resolve the loan purpose for a servicer apply link from its URL param.
 * Defaults to Refinance when missing or unrecognized.
 */
export function resolveServicerLoanPurpose(
  param: string | undefined
): LoanPurpose {
  if (!param) return LoanPurpose.Refinance

  return (
    servicerLoanPurposeParamMap[param.toLowerCase()] ?? LoanPurpose.Refinance
  )
}
